import { ApiError } from '../../api/errors';
import { request } from '../../api/http';
import { asArray, asPage } from '../../api/normalise';
import { isMissingRoute, tolerateMissingRoute, type MaybeMissing } from '../../api/route-missing';
import type { Id } from '../../api/types';

/**
 * The settings surface, read off `tbs-planhub-api` at commit `1440a3b`.
 *
 * 🔴🔴 THE WRITE HALF DOES NOT EXIST YET, AND THIS FILE DOES NOT PRETEND OTHERWISE.
 *
 * `src/routes/settings.ts` serves exactly three GETs and — this is the useful part — advertises the
 * gap itself:
 *
 *     GET /api/v1/settings → { collections: [...], writable: false }
 *
 * with the API's own comment saying why: *"Advertised as read-only rather than silently missing: a
 * client that finds no POST should know it is unbuilt, not conclude it is forbidden."*
 *
 * ⇒ ⭐⭐ THE CAPABILITY PROBE IS THE GATE. Every write control on this screen is disabled by that
 * flag and says so, naming the endpoint that has to land. ⛔ Nothing here mocks a write, and no
 * control is quietly hidden — a hidden button teaches nobody that the feature is unbuilt, and the
 * next person re-derives the same finding from scratch.
 *
 * The write calls below are written against the contract in the API's README §Settings so they are
 * ready the day it ships:
 *
 *     POST   /api/v1/settings/{collection}                 create
 *     PATCH  /api/v1/settings/{collection}/{code}          label, colour, sort_order, is_active
 *     GET    /api/v1/settings/transitions                  the from→to matrix
 *     PUT    /api/v1/settings/transitions                  replace the matrix
 *     GET    /api/v1/projects/{id}/modules  · POST · PATCH
 *
 * ⚠️⚠️ TWO GAPS FOUND WHILE WRITING THIS. ONE IS FIXED, ONE IS STILL OPEN:
 *
 *   1. ✅ FIXED — `PUT` was missing from the API's CORS allow-list, so the transition-matrix PUT was
 *      refused at the PREFLIGHT and surfaced as a network-level failure: a healthy API reading as
 *      unreachable. Only findable in a real browser, which is where it was found. `app.ts` now
 *      registers `['GET','POST','PUT','PATCH','DELETE','OPTIONS']` and its test asserts the whole
 *      method set, so the next route using an unlisted verb goes red there instead.
 *   2. ⛔ STILL OPEN — `modules` IS NOT ONE OF `COLLECTIONS`. The module list is per project and lives
 *      at `/projects/{id}/modules`, which is not built. `GET /settings/modules` answers 404
 *      `unknown settings collection`, which is a DIFFERENT 404 from `no such route` — see
 *      `ModulesPanel`.
 *
 * ⛔ THERE IS NO DELETE FUNCTION IN THIS MODULE, BY CONSTRUCTION. The API README's rule is
 * "deactivate, never delete", and `DELETE /settings/{collection}/{code}` exists there only to answer
 * 409. A client function for it would be a loaded gun on a screen whose entire safety story is that
 * retirement is reversible. `settings-no-delete.test.ts` asserts this file never grows one.
 */

// ── The collections ───────────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ `modules` IS NOT A SETTINGS COLLECTION on the wire — it is per project and has its own path.
 * It is in this union because it is a TAB on this screen, and the panel that renders it goes to a
 * different endpoint. `SETTINGS_COLLECTIONS` below is the wire-level set.
 */
export type CollectionName =
  | 'statuses'
  | 'lanes'
  | 'types'
  | 'priorities'
  | 'impacts'
  | 'resolutions'
  | 'modules';

export const SETTINGS_COLLECTIONS = [
  'statuses',
  'lanes',
  'types',
  'priorities',
  'impacts',
  'resolutions',
] as const;

/**
 * One vocabulary row, widened across the six collections.
 *
 * ⭐ Optional members rather than a discriminated union on purpose: the SHARED shape (label · code ·
 * colour · active · order) is what the list renders, and every collection has it. The extras
 * (`lane`, `is_initial`, `icon`, `is_subtask`) are read where they matter and are `undefined` — not
 * `false` — where the collection does not carry them. ⛔ Defaulting `is_initial` to `false` would
 * make "this API build does not send the flag" indistinguishable from "this status is not initial",
 * and the initial status is one of the three rows the API refuses to deactivate.
 */
export interface VocabRow {
  code: string;
  label: string;
  sort_order: number;
  is_active: boolean;
  colour?: string | null;
  lane?: string;
  is_initial?: boolean;
  is_final?: boolean;
  icon?: string | null;
  is_subtask?: boolean;
}

/** `GET /api/v1/settings` — the capability probe. */
export interface SettingsCapability {
  collections: string[];
  /**
   * 🔴 `false` today, from the API's own mouth. Read as "the write routes are UNBUILT", never as
   * "you are not allowed" — the second is a 403 and looks completely different to a reader.
   */
  writable: boolean;
}

/**
 * ⚠️ If even the probe is missing (an older deployment), we do NOT assume writability either way; we
 * report that we could not ask. `writable: false` and "we do not know" both disable the controls, but
 * they say different things on screen, and only one of them is a fact about the server.
 */
export type CapabilityResult =
  | { kind: 'ok'; capability: SettingsCapability }
  | { kind: 'endpoint-missing'; path: string };

export async function fetchSettingsCapability(signal?: AbortSignal): Promise<CapabilityResult> {
  const result = await tolerateMissingRoute('/settings', () =>
    request<unknown>('/settings', { signal }),
  );
  if (result.kind === 'endpoint-missing') return result;

  const body = result.value;
  const record = (body ?? {}) as Record<string, unknown>;
  return {
    kind: 'ok',
    capability: {
      collections: Array.isArray(record.collections) ? (record.collections as string[]) : [],
      // ⛔ Strict `=== true`. A missing key is not a permission — an absent flag means the build
      // predates the probe, and treating that as writable would enable buttons that 404.
      writable: record.writable === true,
    },
  };
}

export async function fetchCollection(
  collection: Exclude<CollectionName, 'modules'>,
  signal?: AbortSignal,
): Promise<VocabRow[]> {
  const path = `/settings/${collection}`;
  const body = await request<unknown>(path, { signal });
  return asArray<VocabRow>(body, path);
}

// ── Transitions ───────────────────────────────────────────────────────────────────────────────────

/** One edge of `GET /api/v1/settings/transitions` → `{ items: [{from_status, to_status, note}] }`. */
export interface TransitionEdge {
  from_status: string;
  to_status: string;
  note: string | null;
}

export async function fetchTransitionMatrix(signal?: AbortSignal): Promise<TransitionEdge[]> {
  const path = '/settings/transitions';
  const body = await request<unknown>(path, { signal });
  return asArray<TransitionEdge>(body, path);
}

/**
 * Replace the WHOLE matrix.
 *
 * 🔴 The contract is a replace, not a patch, so the caller must send every edge it wants to survive —
 * see `applyTargets` in `transitions-model.ts`, which is the only thing allowed to build this
 * payload. Sending just the edited row's edges would silently delete the other 25 statuses' rules,
 * and the screen would look like it had saved.
 */
export async function replaceTransitionMatrix(items: TransitionEdge[]): Promise<void> {
  await request<unknown>('/settings/transitions', { method: 'PUT', body: { items } });
}

// ── Writes ────────────────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 `code` IS PRESENT HERE AND ONLY HERE. It is settable at creation and never again — tickets,
 * `ticket_status_history` and `status_transition` all reference it by value, so a rename does not
 * rename anything: it re-points every historical row at a meaning that did not exist when the row was
 * written. The row editor has no code field at all; this is the single place the word appears in a
 * request body, which is what makes "immutable after creation" a property of the module rather than a
 * rule someone has to remember.
 */
export interface CreateVocabBody {
  code: string;
  label: string;
  colour?: string | null;
  lane?: string;
  sort_order?: number;
}

export async function createVocabRow(
  collection: Exclude<CollectionName, 'modules'>,
  body: CreateVocabBody,
): Promise<void> {
  await request<unknown>(`/settings/${collection}`, { method: 'POST', body });
}

/**
 * ⛔ NO `code` KEY. Not "optional" — absent. A caller cannot ask for a rename because the type does
 * not let them express one.
 */
export interface UpdateVocabBody {
  label?: string;
  colour?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export async function updateVocabRow(
  collection: Exclude<CollectionName, 'modules'>,
  code: string,
  body: UpdateVocabBody,
): Promise<void> {
  await request<unknown>(`/settings/${collection}/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    body,
  });
}

// ── Modules — per project, a different endpoint ───────────────────────────────────────────────────

export interface ModuleRow {
  id: Id;
  code: string;
  label: string;
  is_active: boolean;
}

export function modulesPath(projectId: Id): string {
  return `/projects/${projectId}/modules`;
}

/**
 * ⚠️ TWO WAYS THIS IS ABSENT, and they are told apart:
 *   · `no such route`                 → `/projects/:id/modules` is not deployed
 *   · `unknown settings collection`   → somebody looked for modules under /settings, where they are
 *                                       not and never will be (db/004 keys `module_def` by project)
 * The second cannot arise from this function; it is documented here because the README's endpoint
 * list invites the mistake.
 */
export async function fetchModules(
  projectId: Id,
  signal?: AbortSignal,
): Promise<MaybeMissing<ModuleRow[]>> {
  const path = modulesPath(projectId);
  return tolerateMissingRoute(path, async () =>
    asArray<ModuleRow>(await request<unknown>(path, { signal }), path),
  );
}

export async function createModule(
  projectId: Id,
  body: { code: string; label: string },
): Promise<void> {
  await request<unknown>(modulesPath(projectId), { method: 'POST', body });
}

export async function updateModule(
  projectId: Id,
  id: Id,
  body: { label?: string; is_active?: boolean },
): Promise<void> {
  await request<unknown>(`${modulesPath(projectId)}/${id}`, { method: 'PATCH', body });
}

// ── Usage counts ──────────────────────────────────────────────────────────────────────────────────

/**
 * 🔴 HOW MANY TICKETS USE THIS ROW — the number that has to be on screen BEFORE the deactivate
 * click, not in the error afterwards.
 *
 * ⭐ It is DERIVED FROM A REAL QUERY, not invented: `GET /tickets?status=x&limit=1` returns the
 * API's own `total` for that filter. `limit=1` because we want the count, not the rows.
 *
 * ⛔ AND IT DOES NOT EXIST FOR EVERY COLLECTION. `GET /tickets` filters by `status`, `type` and
 * `priority` and by nothing else — there is no `impact` filter, no `lane` filter and no `resolution`
 * filter. So impacts and resolutions get `unavailable` with the reason printed, never a `0`. A zero
 * there would read as "safe to retire, nothing uses it", which is the exact wrong conclusion to hand
 * somebody one click before a destructive-looking action.
 */
export type TicketFilterKey = 'status' | 'type' | 'priority';

export async function countTicketsUsing(
  filter: TicketFilterKey,
  code: string,
  signal?: AbortSignal,
): Promise<number> {
  const body = await request<unknown>('/tickets', {
    signal,
    query: { [filter]: code, limit: 1, offset: 0 },
  });
  return asPage<unknown>(body, `/tickets?${filter}=${code}`, { page: 1, per_page: 1 }).total;
}

/**
 * ⭐ A refusal from the API, kept whole.
 *
 * The API independently refuses to deactivate the last active status, the initial status, or one
 * still referenced by an active transition — and that is where those rules LIVE. This client renders
 * the refusal; it does not re-implement the checks in order to grey a toggle out, because a
 * re-implementation is a second copy of a rule that can drift, and the drifted copy always looks
 * right on screen.
 */
export function refusalReason(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.kind !== 'conflict' && error.kind !== 'forbidden') return null;
  return error.reason ?? error.message;
}

/** True when a write failed because the route is not deployed — worth saying differently from a bug. */
export function writeRouteMissing(error: unknown): boolean {
  return isMissingRoute(error);
}
